import * as React from "react";
import styled, { css, keyframes } from "styled-components";
import { theme } from "../../theme";
import BodySmall from "../atoms/typography/BodySmall";
import { WarningIcon } from "../atoms/icons/WarningIcon";
import { CheckmarkIconSmall } from "../atoms/icons/CheckmarkIcons";
import { VerticalSpacer } from "../atoms/Spacers";
import { InfoIconSmall } from "../atoms/icons/InfoIcons";
import { CrossIcon } from "../atoms/icons/CrossIcon";
import { Row, Visible, Hidden } from "react-grid-system";

interface Props {
  title: string;
  message?: string | React.ReactNode;
  type?: "error" | "success" | "warning" | "info" | "issue";
  small?: boolean;
  onClickClose?: () => void;
  version?: "component" | "snackbar";
}

const slideInFromTopRight = keyframes`
  from {
    opacity: 0;
    transform: translate3d(calc(100% + 24px), -16px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

const colorMap = {
  error: theme.colors.error,
  success: theme.colors.success,
  warning: theme.colors.warning,
  info: theme.colors.info,
  issue: theme.colors.error,
};

const Container = styled.div<{
  type?: "error" | "success" | "warning" | "info" | "issue";
  small?: boolean;
  $hasMessage?: boolean;
  $snackbar?: boolean;
}>`
  position: relative;
  background-color: ${({ type }) =>
    type === "error"
      ? theme.colors.error05
      : type === "success"
        ? theme.colors.success05
        : type === "warning"
          ? theme.colors.warning05
          : type === "issue"
            ? theme.colors.white
            : theme.colors.info05};
  border: 1px solid
    ${({ type }) =>
      type === "error"
        ? theme.colors.error20
        : type === "success"
          ? theme.colors.success20
          : type === "warning"
            ? theme.colors.warning20
            : type === "issue"
              ? theme.colors.error20
              : theme.colors.info20};
  border-radius: ${({ small }) => (small ? "8px" : "12px")};
  padding: ${({ small }) => (small ? "2px 8px" : "1rem")};
  display: flex;
  align-items: ${({ $hasMessage }) => ($hasMessage ? "flex-start" : "center")};
  width: fit-content;
  margin-top: ${({ small }) => (small ? "0" : "1rem")};
  margin-bottom: ${({ small }) => (small ? "0" : "1rem")};

  ${({ $snackbar }) =>
    $snackbar &&
    css`
      position: fixed;
      top: 84px;
      right: 24px;
      z-index: 1100;
      margin: 0;
      max-width: calc(100vw - 48px);
      box-shadow: ${theme.shadow.cardShadow};
      animation: ${slideInFromTopRight} 300ms ease-out;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}
`;

const CircleContainer = styled.div<{
  type?: "error" | "success" | "warning" | "info" | "issue";
}>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${({ type }) => colorMap[type || "info"]};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  margin-right: 1rem;
`;

const Title = styled.div<{
  type?: "error" | "success" | "warning" | "info" | "issue";
}>`
  font-weight: 700;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${({ type }) =>
    type === "error"
      ? theme.colors.error
      : type === "success"
        ? theme.colors.success
        : type === "warning"
          ? theme.colors.warning
          : type === "issue"
            ? theme.colors.error
            : theme.colors.text};
`;

const CrossIconContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const ContentColumn = styled.div<{ $hasClose?: boolean }>`
  position: relative;
  min-width: 0;
  padding-right: ${({ $hasClose }) => ($hasClose ? "32px" : "0")};
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 18px;
  flex-shrink: 0;
`;

const Alert: React.FC<Props> = ({
  title,
  message,
  type = "info",
  small = false,
  onClickClose,
  version = "component",
}) => {
  const SmallIcon =
    type === "error"
      ? WarningIcon
      : type === "success"
        ? CheckmarkIconSmall
        : type === "warning"
          ? WarningIcon
          : type === "info"
            ? InfoIconSmall
            : type === "issue"
              ? WarningIcon
              : null;

  const Color = colorMap[type];

  return (
    <>
      <Visible xs>
        <Container
          type={type}
          small={small}
          $hasMessage={!!message}
          $snackbar={version === "snackbar"}
        >
          <ContentColumn $hasClose={!!onClickClose}>
            <Row nogutter align="center">
              {SmallIcon && (
                <>
                  <SmallIcon color={Color} />
                  <VerticalSpacer spacing={0.5} />
                </>
              )}
              <Title type={type}>{title}</Title>
            </Row>
            <BodySmall>{message}</BodySmall>
            {onClickClose && (
              <CrossIconContainer onClick={onClickClose}>
                <CrossIcon />
              </CrossIconContainer>
            )}
          </ContentColumn>
        </Container>
      </Visible>
      <Hidden xs>
        <Container
          type={type}
          small={small}
          $hasMessage={!!message}
          $snackbar={version === "snackbar"}
        >
          {small ? (
            <>
              {SmallIcon && (
                <IconWrapper>
                  <SmallIcon color={Color} />
                </IconWrapper>
              )}
              <VerticalSpacer spacing={0.5} />
            </>
          ) : (
            <CircleContainer type={type}>
              {SmallIcon && <SmallIcon color={theme.colors.white} />}
            </CircleContainer>
          )}
          <ContentColumn $hasClose={!!onClickClose}>
            <Title type={type}>{title}</Title>
            <BodySmall>{message}</BodySmall>
            {onClickClose && (
              <CrossIconContainer
                onClick={onClickClose}
                aria-label="Close alert"
              >
                <CrossIcon />
              </CrossIconContainer>
            )}
          </ContentColumn>
        </Container>
      </Hidden>
    </>
  );
};

export default Alert;
